import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { COMPANY } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export const Contact = ({ services = [] }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      errs.name = "Please enter your name";
    if (!EMAIL_RE.test(form.email.trim()))
      errs.email = "Please enter a valid email address";
    if (!form.message.trim() || form.message.trim().length < 5)
      errs.message = "Please tell us a little about what you need";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service || "General Enquiry",
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Enquiry sent! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or email us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="scroll-mt-20 py-16 md:py-24 hero-gradient noise-overlay"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <p className="text-sm font-medium text-primary tracking-wide uppercase">Contact Us</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground mt-3">
            Let's talk about your project
          </h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            Describe your business and what you need — we'll reply within 24 hours with a
            clear plan. The contact form is the fastest way to reach us.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-4"
          >
            {[
              {
                icon: Phone,
                label: "Phone",
                value: COMPANY.phone,
                href: `tel:${COMPANY.phoneIntl}`,
                testid: "contact-info-phone",
              },
              {
                icon: Mail,
                label: "Email",
                links: [
                  { value: COMPANY.email, href: `mailto:${COMPANY.email}` },
                  { value: COMPANY.email2, href: `mailto:${COMPANY.email2}` },
                ],
                testid: "contact-info-email",
              },
              {
                icon: MapPin,
                label: "Registered Office",
                value: COMPANY.address,
                testid: "contact-info-address",
              },
              {
                icon: Clock,
                label: "Response Time",
                value: "Within 24 hours, Monday to Saturday",
                testid: "contact-info-hours",
              },
            ].map((item) => (
              <div key={item.label} className="glass-card p-5 flex items-start gap-4" data-testid={item.testid}>
                <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center shrink-0">
                  <item.icon className="h-4.5 w-4.5 text-primary h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">{item.label}</p>
                  {item.links ? (
                    item.links.map((link) => (
                      <a
                        key={link.value}
                        href={link.href}
                        className="text-sm text-foreground hover:text-primary transition-colors font-medium mt-1 block"
                      >
                        {link.value}
                      </a>
                    ))
                  ) : item.href ? (
                    <a href={item.href} className="text-sm text-foreground hover:text-primary transition-colors font-medium mt-1 block">
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-sm text-foreground font-medium mt-1">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="glass-card p-6 md:p-8">
              {sent ? (
                <div className="text-center py-12" data-testid="contact-success-message">
                  <div className="h-16 w-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mt-5">
                    Enquiry received!
                  </h3>
                  <p className="text-muted-foreground mt-2 max-w-sm mx-auto">
                    Thank you for getting in touch. We'll reply to your email within 24 hours.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSent(false)}
                    className="mt-6 rounded-xl border-white/10 bg-white/5 hover:bg-white/10"
                    data-testid="contact-send-another-button"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name" className="text-foreground">
                        Full name *
                      </Label>
                      <Input
                        id="contact-name"
                        placeholder="John Smith"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        className={`h-12 bg-white/5 border-white/10 ${errors.name ? "border-destructive" : ""}`}
                        data-testid="contact-name-input"
                      />
                      {errors.name && (
                        <p className="text-xs text-destructive" data-testid="contact-name-error">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email" className="text-foreground">
                        Email *
                      </Label>
                      <Input
                        id="contact-email"
                        type="email"
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        className={`h-12 bg-white/5 border-white/10 ${errors.email ? "border-destructive" : ""}`}
                        data-testid="contact-email-input"
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive" data-testid="contact-email-error">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone" className="text-foreground">
                        Phone <span className="text-muted-foreground">(optional)</span>
                      </Label>
                      <Input
                        id="contact-phone"
                        placeholder="07XXX XXXXXX"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        className="h-12 bg-white/5 border-white/10"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Service interest</Label>
                      <Select value={form.service} onValueChange={(v) => setField("service", v)}>
                        <SelectTrigger
                          className="h-12 bg-white/5 border-white/10 text-foreground"
                          data-testid="contact-service-select"
                        >
                          <SelectValue placeholder="Choose a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10 max-h-72">
                          {services.map((s) => (
                            <SelectItem key={s.id} value={s.title}>
                              {s.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="General Enquiry">Other / General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message" className="text-foreground">
                      Your message *
                    </Label>
                    <Textarea
                      id="contact-message"
                      placeholder="Tell us about your business and what you need..."
                      rows={5}
                      value={form.message}
                      onChange={(e) => setField("message", e.target.value)}
                      className={`bg-white/5 border-white/10 resize-none ${errors.message ? "border-destructive" : ""}`}
                      data-testid="contact-message-input"
                    />
                    {errors.message && (
                      <p className="text-xs text-destructive" data-testid="contact-message-error">{errors.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-xl text-base font-medium"
                    data-testid="contact-form-submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Send Enquiry <Send className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
